import React, { useEffect, useRef } from 'react';
import { Box, Typography, Stack, Paper } from "@mui/material";
import { useLocation } from "react-router-dom";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import { apiClient } from "../utils/apiClient";

export function SuccessInvoicePage() {
  const location = useLocation();
  const paymentInfo = location.state?.paymentInfo;
  const fallbackInvoiceNumber = location.state?.invoiceNumber?.trim();
  const currency = paymentInfo?.currency || 'USD';

  const formatText = (value?: string | null): string => {
    if (!value) {
      return '—';
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '—';
  };

  const formatCurrency = (value?: string | null): string => {
    if (!value) {
      return '—';
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value;
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(numericValue);
    } catch {
      return `$${numericValue.toFixed(2)}`;
    }
  };

  const details = [
    { label: 'Name', value: formatText(paymentInfo?.name) },
    { label: 'Company', value: formatText(paymentInfo?.company) },
    { label: 'Email Address', value: formatText(paymentInfo?.email) },
    { label: 'Amount', value: formatCurrency(paymentInfo?.amount) },
    {
      label: 'Invoice Number',
      value: formatText(paymentInfo?.invoiceNumber || fallbackInvoiceNumber),
    },
    {
      label: 'Other information',
      value: formatText(paymentInfo?.otherInformation),
      preserveWhitespace: true,
    },
    { label: 'Payment amount', value: formatCurrency(paymentInfo?.paymentAmount) },
    { label: 'Payment method', value: formatText(paymentInfo?.paymentMethod) },
  ];

  const hasPaymentInfo = Boolean(paymentInfo);
  const notificationSentRef = useRef(false);

  useEffect(() => {
    if (!paymentInfo || notificationSentRef.current) {
      return;
    }

    notificationSentRef.current = true;

    const notify = async () => {
      try {
        await apiClient.post('/invoice/payment_notification', { paymentInfo });
      } catch (error) {
        notificationSentRef.current = false;
        console.error('Failed to send invoice payment notification', error);
      }
    };

    notify();
  }, [paymentInfo]);

  return (
    <div>
      <MenuBar />
      <Stack spacing={2} sx={{ 
        py: 8, 
        pb: 20, 
        maxWidth: { xs: '100%', md: '100%', lg: '100%' },
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        textAlign: 'center'
      }}>
        <Box sx={{ mt: 16, mb: 4 }}>
          <Typography variant="h4">
            Thank you for your payment!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {hasPaymentInfo
              ? 'Here is a summary of your payment.'
              : 'We could not find your payment details. Please contact support if this is unexpected.'}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            textAlign: 'left',
            mx: 'auto',
            width: '100%',
            maxWidth: { xs: '100%', sm: '540px' },
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: 'background.paper',
          }}
        >
          <Stack spacing={3}>
            {details.map(({ label, value, preserveWhitespace }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ minWidth: { sm: '160px' } }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: { xs: 'left', sm: 'right' },
                    whiteSpace: preserveWhitespace ? 'pre-line' : 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Stack>
      <Footer />
    </div>
  );
}
